import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Tree } from "@minoru/react-dnd-treeview";
import {
	DragLayerMonitorProps,
	NodeModel,
} from "@minoru/react-dnd-treeview/dist/types";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import {
	MouseEvent as ReactMouseEvent,
	useEffect,
	useMemo,
	useState,
} from "react";

import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";

import { HelmetDexteel } from "@dexteel/mesf-core";
import { CatalogLocation } from "../../../models/CatalogLocation";
import { FileProperties } from "../../../models/tree/FileProperties";
import { TreeParameters } from "../../../models/tree/TreeParameter";
import { useCatalogLocationContext } from "../context/CatalogLocationContext";
import { useLocationOptionsFunctions } from "../hooks/useLocationOptionsFunctions";
import { useLocationActions } from "../hooks/useLocationsActions";
import { useSearchLocations } from "../hooks/useSearchLocations";
import { CustomNode } from "./CustomNode";
import { CustomDragPreview } from "./DragPreview/CustomDragPreview";
import { MultipleDragPreview } from "./DragPreview/MultipleDragPreview";
import { UpsertDeleteViewLocation } from "./UpsertDeleteViewLocation";

export const TreeViewLocation = () => {
	const [isCtrlPressing, setIsCtrlPressing] = useState(false);
	const [showModal, setShowModal] = useState<
		"NEW" | "EDIT" | "DELETE" | "VIEW" | ""
	>("");
	const [selectedNodes, setSelectedNodes] = useState<
		NodeModel<FileProperties>[]
	>([]);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string>("");
	const [updatedSuccessfullyMessage, setUpdatedSuccessfullyMessage] =
		useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [locationId, setLocationId] = useState<number | null>();

	const { searchLocations } = useSearchLocations({
		setSearchError: setError,
		setAllLocationLoading: setIsLoading,
	});

	const {
		state: { allLocationNodes, allLocations, openLocations },
		actions: { setLocationNodeSelectedInTree },
	} = useCatalogLocationContext();

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useLocationOptionsFunctions({
		setLocationId,
		setShowModal,
	});

	const { handleDrop, handleCtrlClick, handleDragStart, handleDragEnd } =
		useLocationActions({
			location: allLocationNodes,
			selectedNodes,
			setSelectedNodes,
			setIsDragging,
			isCtrlPressing,
			setIsCtrlPressing,
			onLocationUpdateStart: () => {
				setIsLoading(true);
			},
			onLocationUpdateEnd: (result) => {
				if (result.ok) {
					setUpdatedSuccessfullyMessage("The location was updated succesfully");
					searchLocations();
				} else {
					setError(result.message);
				}
			},
		});

	const handleContextMenu = (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		if (e.type === "contextmenu") {
			setSelectedNodes([data]);
			showContextMenu(e, data, "locationContext");
			const selectedNode = allLocations.find(
				(node: CatalogLocation) => node.LocationId === data.id,
			);
			if (selectedNode) {
				setLocationNodeSelectedInTree(selectedNode);
			}
		}
	};

	const onNodeDoubleClicked = (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		setLocationId((data as TreeParameters).id);
		setShowModal("EDIT");
		const selectedNode = allLocations.find(
			(node: CatalogLocation) =>
				node.LocationId === (data as TreeParameters).id,
		);
		if (selectedNode) {
			setLocationNodeSelectedInTree(selectedNode);
		}
	};

	const onModalHide = (shouldUpdate: boolean) => {
		if (shouldUpdate) searchLocations();
		setShowModal("");
	};

	const openLocationsArray = useMemo(() => {
		return Object.keys(openLocations).map((el) => parseInt(el));
	}, [openLocations]);

	useEffect(() => {
		searchLocations();
		registerConfig({
			id: "locationContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<HelmetDexteel title="Locations Tree" />
			<Grid2
				container
				p={1}
				spacing={1}
				onContextMenu={(e) => {
					e.preventDefault();
					showContextMenu(e, null, "locationContext");
				}}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Locations Tree
					</Typography>
				</Grid2>
				{isLoading ? (
					<CenteredLazyLoading />
				) : (
					<Paper
						elevation={2}
						sx={{
							width: "100%",
							borderRadius: "0.75rem",
							padding: 2,
						}}
					>
						<Box
							sx={{
								height: "60vh",
								width: "100%",
								overflowY: "auto",
								paddingBottom: 10,
								"& .root": {
									boxSizing: "border-box",
									height: "100%",
									padding: 2,
								},
								"& .dropTarget": {
									boxShadow: "0 0 0 1px #1967d2 inset",
								},
							}}
						>
							<Tree
								rootId={0}
								initialOpen={openLocationsArray}
								tree={allLocationNodes}
								enableAnimateExpand={true}
								classes={{
									root: "root",
									dropTarget: "dropTarget",
								}}
								onDrop={handleDrop}
								onDragStart={handleDragStart}
								onDragEnd={handleDragEnd}
								canDrop={(location: any, options: any): boolean => {
									return !selectedNodes.some(
										(selectedNode) => selectedNode.id === options.dropTargetId,
									);
								}}
								render={(node, options) => {
									const selected = selectedNodes.some(
										(selectedNode) => selectedNode.id === node.id,
									);
									return (
										<CustomNode
											setContextMenuOver={handleContextMenu}
											node={node}
											{...options}
											isSelected={selected}
											isDragging={selected && isDragging}
											onClick={handleCtrlClick}
											onDoubleClick={onNodeDoubleClicked}
										/>
									);
								}}
								dragPreviewRender={(
									monitorProps: DragLayerMonitorProps<FileProperties>,
								) => {
									if (selectedNodes.length > 1) {
										return <MultipleDragPreview dragSources={selectedNodes} />;
									}
									return <CustomDragPreview monitorProps={monitorProps} />;
								}}
							/>
						</Box>
					</Paper>
				)}
				<FooterButton
					label="LOCATION"
					showCreateModal={showModal === "NEW"}
					setShowCreateModal={(showCreateModal) =>
						showCreateModal ? setShowModal("NEW") : setShowModal("")
					}
				/>
			</Grid2>
			<NotificationSnackBar
				message={updatedSuccessfullyMessage}
				onHide={() => setUpdatedSuccessfullyMessage("")}
			/>
			<UpsertDeleteViewLocation
				LocationId={locationId}
				show={showModal !== ""}
				modal={showModal}
				onHide={onModalHide}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
